'use client';

import { useState, useEffect } from 'react';
import { AppImage } from '@/components/common/AppImage';
import { Card } from '@/components/common/Card';
import { Wallet, Users, Tag, CheckCircle, ExternalLink } from 'lucide-react';
import { VerificationBadge } from '@/components/common/VerificationBadge';
import type { Player, Tournament } from '@/lib/types/auction';
import { formatCurrency } from '@/lib/utils/format';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { Tabs } from '@/components/common/Tabs';
import { StatusBadge } from '@/components/common/StatusBadge';
import { FormSelect } from '@/components/forms/FormSelect';
import { PlayerActions } from './PlayerActions';
import { handleError } from '@/lib/utils/error';
import { tournamentService } from '@/services/api/tournaments';
import { ShareButton } from '@/components/common/ShareButton';
import { SportSpecificDetails } from '@/components/common/SportSpecificDetails';

interface PlayerDetailsProps {
  player: Player;
  tournament?: Tournament;
  onAction: () => void;
  onAssignPool?: (poolId: string) => void;
  availablePools?: Array<{ id: string; name: string }>;
}

interface StatusVariant {
  color: string;
  icon: JSX.Element;
  text: string;
  variant: 'success' | 'info' | 'default';
}

export function PlayerDetails({
  player,
  tournament: providedTournament,
  onAssignPool,
  availablePools = [],
}: PlayerDetailsProps) {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState(0);
  const [fetchedTournament, setFetchedTournament] = useState<Tournament | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(
    !providedTournament && !!player.tournamentId
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTournament = async () => {
      if (!providedTournament && player.tournamentId) {
        try {
          const tournamentData = await tournamentService.getById(
            player.tournamentId
          );
          setFetchedTournament(tournamentData);
        } catch (err) {
          setError(handleError(err, 'Loading Tournament'));
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTournament();
  }, [player.tournamentId, providedTournament]);

  const tournament = providedTournament || fetchedTournament;

  /** 🔹 Sport-specific details rendering */
  // Remove the old renderSportSpecificDetails function - replaced with component

  /** 🔹 Navigation Handlers */
  const handleTeamClick = () => {
    if (player.team?.id) {
      router.push(`/team/${player.team.id}`);
    }
  };

  const handlePoolClick = () => {
    if (player.pool?.id) {
      router.push(
        `/dashboard/tournaments/${player.pool.tournamentId}/pools/${player.pool.id}`
      );
    }
  };

  /** 🔹 Enhanced Player Status Styling */
  const statusConfig: Record<string, StatusVariant> = {
    available: {
      color: 'bg-gradient-to-r from-green-500 to-green-600',
      icon: <CheckCircle className="mr-1 h-4 w-4" />,
      text: 'AVAILABLE',
      variant: 'success',
    },
    sold: {
      color: 'bg-gradient-to-r from-accent-blue to-primary-600',
      icon: <CheckCircle className="mr-1 h-4 w-4" />,
      text: player.role === 'icon' ? 'ICON PLAYER' : 'SOLD',
      variant: 'info',
    },
    unsold: {
      color: 'bg-gradient-to-r from-gray-500 to-gray-600',
      icon: <CheckCircle className="mr-1 h-4 w-4" />,
      text: 'UNSOLD',
      variant: 'default',
    },
  };

  const renderTabContent = () => {
    const renderInfo = () => (
      <div className="space-y-4 p-2 sm:p-4">
        {/* 🔹 Player Base Price */}
        <div className="flex items-center space-x-2 rounded-lg bg-gradient-to-r from-accent-green/10 to-accent-green/20 px-4 py-3 text-lg font-semibold text-gray-800 shadow-sm">
          <Wallet className="h-5 w-5 text-accent-green" />
          <span>
            {player.role === 'icon'
              ? `Icon Player: ${formatCurrency(player.soldPrice || 0)}`
              : formatCurrency(player.basePrice)}
          </span>
        </div>

        {/* Sport-specific details */}
        <SportSpecificDetails
          sportDetails={
            player.sportDetails ||
            player.user?.sportDetails?.[tournament?.sport?.toLowerCase() || '']
          }
          sport={tournament?.sport}
          theme="card"
        />

        {/* Team & Tournament Info (if player is sold) */}
        {player.status === 'sold' && player.team && player.soldPrice && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={handleTeamClick}
            className="cursor-pointer rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                Team Information
              </h3>
              <ExternalLink className="h-4 w-4 text-primary-500" />
            </div>
            <div className="flex items-center space-x-3">
              {player.team.logo && (
                <div className="h-12 w-12 overflow-hidden rounded-full border-2 border-white shadow-md">
                  <AppImage
                    src={player.team.logo}
                    alt={player.team.name}
                    gradient
                    fallbackText={player.team.name}
                  />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">{player.team.name}</p>
                <p className="text-sm text-gray-600">
                  Purchased for:{' '}
                  <span className="font-semibold text-accent-green">
                    {formatCurrency(player.soldPrice)}
                  </span>
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* 🔹 Pool Information */}
        {player.pool && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={handlePoolClick}
            className="cursor-pointer rounded-lg bg-gradient-to-r from-purple-50 to-purple-100 p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                Pool Information
              </h3>
              <ExternalLink className="h-4 w-4 text-primary-500" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex items-center space-x-2 rounded-md bg-white/80 p-2">
                <Tag className="h-4 w-4 text-accent-purple" />
                <span className="text-sm text-gray-700">Pool Name:</span>
                <span className="text-sm font-medium text-gray-900">
                  {player.pool.name}
                </span>
              </div>
              <div className="flex items-center space-x-2 rounded-md bg-white/80 p-2">
                <Users className="h-4 w-4 text-accent-purple" />
                <span className="text-sm text-gray-700">Role:</span>
                <span className="text-sm font-medium capitalize text-gray-900">
                  {player.pool.playerRole}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* 🔹 Pool Assignment Section - Only show if player is not assigned and pools are available */}
        {player.status === 'available' &&
          onAssignPool &&
          availablePools.length > 0 && (
            <div className="mt-6 rounded-lg bg-gradient-to-r from-primary-50 to-primary-100 p-4 shadow-sm">
              <h3 className="text-sm font-medium text-gray-900">
                Assign to Pool
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Select a pool to assign this player
              </p>
              <FormSelect
                label="Pool"
                name="poolId"
                className="mt-2"
                options={[
                  { value: '', label: 'Select Pool' },
                  ...availablePools.map((pool) => ({
                    value: pool.id,
                    label: pool.name,
                  })),
                ]}
                onChange={(e) => onAssignPool(e.target.value)}
                defaultValue=""
              />
            </div>
          )}
      </div>
    );

    return (
      <div className="space-y-6">
        {/* 🔹 Player Header Card */}
        <Card allowOverflow className="p-0 md:p-0">
          <div className="relative rounded-lg bg-gradient-to-r from-primary-500 to-accent-blue p-2 text-white sm:p-4">
            <div className="flex flex-col items-center space-y-4 sm:flex-row sm:space-x-6 sm:space-y-0">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative h-24 w-24 overflow-hidden rounded-full border-4 border-white/70 shadow-lg sm:h-32 sm:w-32"
              >
                <AppImage
                  src={player.user?.profilePicture || ''}
                  alt={player.user?.name || 'Player'}
                  shape="circle"
                  gradient
                  size="lg"
                  className="h-full w-full object-cover"
                  fallbackText={player.user?.name}
                />
              </motion.div>

              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center space-x-2 sm:justify-start">
                  <h1 className="text-2xl font-bold sm:text-3xl">
                    {player.user?.name}
                  </h1>
                  {player.user?.emailVerified && (
                    <VerificationBadge className="text-white" />
                  )}
                </div>

                <div className="mt-2 flex items-center justify-center space-x-2 text-white/90 sm:justify-start">
                  <Tag className="h-4 w-4" />
                  <span>
                    Role: <span className="capitalize">{player.role}</span>
                  </span>
                </div>
              </div>

              {/* Actions Section - Reorganized for Mobile */}
              <div className="absolute right-2 top-2 flex flex-col items-end gap-2 sm:right-4 sm:top-4 sm:flex-row sm:items-center sm:gap-3">
                {/* Status Badge */}
                {player.status && (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    <StatusBadge
                      variant={
                        statusConfig[player.status]?.variant || 'default'
                      }
                      className="px-3 py-1.5 text-sm"
                    >
                      {statusConfig[player.status]?.text || 'UNKNOWN'}
                    </StatusBadge>
                  </motion.div>
                )}

                {/* Action Buttons Container */}
                <div className="flex items-center gap-2">
                  <ShareButton
                    title={`${player.user?.name || 'Player'} - Player Profile`}
                    description={`Check out ${player.user?.name || 'Player'}'s profile on Auction Chase${tournament ? ` from ${tournament.name}` : ''}`}
                    url={`${window.location.origin}/player/${player.id}`}
                    hashtags={['AuctionChase', 'Player']}
                    theme="dark"
                    variant="icon"
                    image={
                      player.user?.profilePicture ||
                      `${window.location.origin}/images/og.jpg`
                    }
                  />

                  {tournament && (
                    <PlayerActions
                      player={player}
                      tournament={tournament}
                      onAction={() => window.location.reload()}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <QuickStat
                icon={<Wallet />}
                label="Base Price"
                value={formatCurrency(player.basePrice)}
              />
            </div>
          </div>
        </Card>

        {/* 🔹 Tabs Section */}
        <Card>
          <Tabs
            items={[{ label: 'Player Info', content: renderInfo() }]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </Card>
      </div>
    );
  };

  return renderTabContent();
}

// Helper Components
interface QuickStatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function QuickStat({ icon, label, value }: QuickStatProps) {
  return (
    <div className="rounded-lg bg-white/10 p-3 text-center">
      <div className="mb-1 flex justify-center">{icon}</div>
      <p className="text-xs font-medium text-white/80">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
